import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { auth, type AdminUser } from '../api/endpoints'
import { ApiError, getToken, setToken } from '../api/client'

interface AuthContextValue {
  user: AdminUser | null
  isLoading: boolean
  isAuthenticated: boolean
  sendOtp: (phone: string) => Promise<{ ok: true; devOtp?: string } | { ok: false; error: string }>
  verifyOtp: (
    phone: string,
    otp: string,
  ) => Promise<{ ok: true } | { ok: false; error: string }>
  logout: () => Promise<void>
  refresh: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setUser(null)
      setIsLoading(false)
      return
    }
    try {
      const { user: me } = await auth.me()
      // Admin and author roles both get into the SPA. Anything else
      // (customer, etc.) gets the token cleared and is bounced to login.
      if (me.role === 'admin' || me.role === 'author') {
        setUser(me)
      } else {
        setUser(null)
        setToken(null)
      }
    } catch (err) {
      // 401 → token cleared by api(); any other error → also clear to be safe
      setToken(null)
      setUser(null)
      if (!(err instanceof ApiError)) console.warn('[auth] refresh failed', err)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    refresh()
  }, [refresh])

  const sendOtp = useCallback(async (phone: string) => {
    try {
      const r = await auth.sendOtp(phone, 'login')
      return { ok: true as const, devOtp: r.devOtp }
    } catch (err) {
      return {
        ok: false as const,
        error: err instanceof Error ? err.message : 'Failed to send OTP',
      }
    }
  }, [])

  const verifyOtp = useCallback(async (phone: string, otp: string) => {
    try {
      const r = await auth.verifyOtp(phone, otp, 'login')
      if (r.user.role !== 'admin' && r.user.role !== 'author') {
        return {
          ok: false as const,
          error: 'This account does not have admin or author access',
        }
      }
      setToken(r.token)
      setUser(r.user)
      return { ok: true as const }
    } catch (err) {
      return {
        ok: false as const,
        error: err instanceof Error ? err.message : 'Verification failed',
      }
    }
  }, [])

  const logout = useCallback(async () => {
    try {
      await auth.logout()
    } catch {
      // ignore — we're clearing locally regardless
    }
    setToken(null)
    setUser(null)
  }, [])

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user && (user.role === 'admin' || user.role === 'author'),
        sendOtp,
        verifyOtp,
        logout,
        refresh,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
